import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { MessageType, MESSAGE_STATUS_INDICATOR } from 'models/Message';
import { Button, Collapsible } from 'ncoded-component-library';
import { ExtractPropTypes } from 'types';
import { t } from 'i18next';
import {
  getHasElementsInArray,
  getIsCurrentUser,
  getMemberInitials,
  getUserFullName,
} from 'utils';
import api from 'api';
import MessageFile from 'components/MessageFiles/components/MessageFile';
import SoftSignalIcon from 'components/SoftSignalIcon';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import SentTicksIcon from 'icons/SentTicks.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useActionOnAtCopy from 'hooks/useActionOnAtCopy';
import useDeleteMessage from 'hooks/useDeleteMessage';
import TrashIcon from 'icons/Trash.icon';
import ReactionPopup from 'components/ReactionPopup';
import { ReactionType } from 'models/Reactions';
import ReactionList from 'components/ReactionList';
import { getReactionNumbers } from '../../helpers';
import MessagingContext from '../../../../providers/Messaging/Messaging.context';
import { getImgUrl } from 'components/ResolveImage/utils';
import useDisplayMessage from 'hooks/useDisplayChatMessage';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import { AvatarColors } from 'models/User';

import './ChatMessage.styles.scss';

type ChatMessageProps = {
  className?: string;
  messageId: string;
  isDirectMessage: boolean;
  isBuyer: boolean;
  buyerId: string;
  companyLogoImg: string;
  companyName: string;
  interlocutorName: string;
  interlocutorImg: string;
  interlocutorColor: AvatarColors;
  prevMsgUserId: string;
  createdBy: string;
  disabled: boolean;
  onDeleteMessageRequest: (messageId: string) => void;
  updateMessage: (message: Partial<MessageType>) => void;
} & Omit<MessageType, '_id'> &
  ExtractPropTypes<typeof Collapsible>;

const ChatMessage: React.FC<ChatMessageProps> = (props) => {
  const {
    messageId,
    className,
    text,
    userId,
    createdAt,
    isDirectMessage,
    isBuyer,
    buyerId,
    companyLogoImg,
    companyName,
    interlocutorImg,
    interlocutorColor,
    interlocutorName,
    prevMsgUserId,
    statusIndicator: indicator,
    showDate = true,
    files,
    createdBy,
    isInterestContext,
    softSignal,
    dataRoomNode,
    dataRoomNodeName,
    seenAt,
    isPulseCheck,
    isDeleted,
    reaction,
    disabled,
    sessionId,
    onDeleteMessageRequest,
    updateMessage,
    ...rest
  } = props;

  const {
    isSuperAdmin,
    currentUser: {
      _id: currentUserId,
      profileImage,
      avatarColor,
      firstName,
      lastName,
    },
  } = useContext(CurrentUserContext);
  const { openNode } = useContext(DocumentViewContext);
  const { dataRoomId, dataRoomName, ownerId, userSide } =
    useContext(MessagingContext);

  const currentUserImg = useMemo(
    () => getImgUrl(profileImage?.url, '100'),
    [profileImage],
  );

  const hasCurrentUserSentMessage = useMemo(
    () => currentUserId === userId,
    [currentUserId, userId],
  );

  const hasCurrentUserSentPrevMessage = useMemo(
    () => currentUserId === prevMsgUserId,
    [currentUserId, prevMsgUserId],
  );

  const isPrevBuyerMsg = useMemo(
    () => buyerId === prevMsgUserId,
    [buyerId, prevMsgUserId],
  );

  const isBuyerMsg = useMemo(() => buyerId === userId, [buyerId, userId]);

  const rightSideAvatar = useMemo(
    () => (isDirectMessage || isBuyer ? currentUserImg : companyLogoImg),
    [companyLogoImg, currentUserImg, isBuyer, isDirectMessage],
  );
  const leftSideAvatar = useMemo(
    () => (isDirectMessage || !isBuyer ? interlocutorImg : companyLogoImg),
    [companyLogoImg, interlocutorImg, isBuyer, isDirectMessage],
  );

  const rightSideName = useMemo(
    () =>
      getMemberInitials(
        isDirectMessage || isBuyer
          ? getUserFullName(firstName, lastName)
          : companyName,
      ),
    [companyName, firstName, isBuyer, isDirectMessage, lastName],
  );
  const leftSideName = useMemo(
    () =>
      getMemberInitials(
        isDirectMessage || !isBuyer ? interlocutorName : companyName,
      ),
    [companyName, interlocutorName, isBuyer, isDirectMessage],
  );

  const rightSideColor = useMemo(
    () => (isDirectMessage || isBuyer ? avatarColor : null),
    [avatarColor, isBuyer, isDirectMessage],
  );
  const leftSideColor = useMemo(
    () => (isDirectMessage || !isBuyer ? interlocutorColor : null),
    [interlocutorColor, isBuyer, isDirectMessage],
  );

  const isMyMessage = useMemo(
    () =>
      isDirectMessage
        ? hasCurrentUserSentMessage
        : (isBuyer && isBuyerMsg) || (!isBuyer && !isBuyerMsg),
    [hasCurrentUserSentMessage, isBuyer, isBuyerMsg, isDirectMessage],
  );

  const isPrevMyMessage = useMemo(
    () =>
      isDirectMessage
        ? hasCurrentUserSentPrevMessage
        : (isBuyer && isPrevBuyerMsg) || (!isBuyer && !isPrevBuyerMsg),
    [hasCurrentUserSentPrevMessage, isBuyer, isDirectMessage, isPrevBuyerMsg],
  );

  const errorIndicator = useMemo(
    () => indicator === MESSAGE_STATUS_INDICATOR.ERROR,
    [indicator],
  );

  const notSentIndicator = useMemo(
    () => indicator === MESSAGE_STATUS_INDICATOR.NOT_SENT,
    [indicator],
  );

  const showSeen = useMemo(
    () => !isDeleted && (isSuperAdmin || isMyMessage) && !notSentIndicator,
    [isDeleted, isMyMessage, isSuperAdmin, notSentIndicator],
  );

  const { thumbsDown, thumbsUp, questionMark } = useMemo(
    () => getReactionNumbers(reaction),
    [reaction],
  );

  const classes = classNames(
    'chat-message',
    {
      'chat-message--sent': isMyMessage,
      'chat-message--received': !isMyMessage,
      'chat-message--error': errorIndicator,
      'chat-message--not-sent': notSentIndicator,
      'chat-message--seen': showSeen,
      'chat-message--deleted': isDeleted,
    },
    className,
  );

  const canDeleteMessage = useDeleteMessage({ createdAt, seenAt });

  const areActionsDisabled = useMemo(
    () => disabled || isDeleted || notSentIndicator,
    [disabled, isDeleted, notSentIndicator],
  );

  const showCanDeleteMessage = useMemo(
    () => !areActionsDisabled && canDeleteMessage && getIsCurrentUser(userId),
    [areActionsDisabled, canDeleteMessage, userId],
  );

  const showCanReactToMessage = useMemo(
    () => !areActionsDisabled && !isMyMessage,
    [areActionsDisabled, isMyMessage],
  );

  const getActionOnAtCopy = useActionOnAtCopy();

  const { getIsEmpty, renderText } = useDisplayMessage();

  const isEmpty = getIsEmpty(text);

  const hasFiles = useMemo(() => getHasElementsInArray(files), [files]);

  const renderedFiles = useMemo(
    () =>
      files?.map(({ chatSessionFileId, name, originalType, type, url }) => (
        <MessageFile
          key={chatSessionFileId}
          id={chatSessionFileId}
          name={name}
          type={originalType || type}
          url={url}
          dataRoomId={dataRoomId}
          dataRoomName={dataRoomName}
          onFilePreview={(id: string) =>
            api.messages.previewMessageFile(sessionId, id)
          }
        />
      )),
    [files, sessionId, dataRoomId, dataRoomName],
  );

  const onReact = useCallback(
    async (nextReaction: ReactionType) => {
      api.messages.addMessageReaction(messageId, nextReaction);
      updateMessage({
        _id: messageId,
        reaction: nextReaction === reaction ? undefined : nextReaction,
      });
    },
    [messageId, reaction, updateMessage],
  );

  if (!hasFiles && isEmpty && !isDeleted) return null;

  return (
    <div className="chat-message__container" data-message-id={`${messageId}`}>
      <Collapsible
        {...rest}
        className={classes}
        trigger={
          <>
            <div className="chat-message__content-wrapper">
              {!isMyMessage ? (
                !isPrevMyMessage && prevMsgUserId ? (
                  <div className="empty-avatar" />
                ) : (
                  <AvatarWithInitials
                    imageUrl={leftSideAvatar}
                    initials={leftSideName}
                    color={leftSideColor}
                  />
                )
              ) : null}

              <div className="chat-message__content">
                {isInterestContext && (
                  <p className="chat-message__content__interest-context">
                    {t('interestContext')}
                  </p>
                )}

                {softSignal && (
                  <div className="chat-message__content__soft-signal">
                    {isPulseCheck && <p>{t('pulseCheck')}:</p>}

                    {(dataRoomNode || dataRoomNodeName) && (
                      <p>{t('softSignal')}:</p>
                    )}
                    {dataRoomNode ? (
                      <Button
                        variant="link"
                        onClick={() =>
                          openNode(dataRoomNode, {
                            dataRoomName,
                            userSide,
                            ownerId,
                          })
                        }
                      >
                        {dataRoomNode?.name}
                      </Button>
                    ) : (
                      dataRoomNodeName && <p>{dataRoomNodeName}</p>
                    )}

                    <SoftSignalIcon softSignal={softSignal} />
                  </div>
                )}

                <div className="chat-message__content__text">
                  {isDeleted ? (
                    t('thisMessageWasDeleted')
                  ) : (
                    <>
                      {showCanDeleteMessage && (
                        <Button
                          icon={TrashIcon}
                          variant="icon"
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteMessageRequest(messageId);
                          }}
                        />
                      )}

                      {showCanReactToMessage && (
                        <ReactionPopup
                          activeReaction={reaction}
                          onReact={onReact}
                        />
                      )}

                      {renderText(text)}

                      {hasFiles && (
                        <div className="chat-message__content__text__files">
                          {renderedFiles}
                        </div>
                      )}

                      {showSeen && (
                        <SentTicksIcon
                          className={classNames({ seen: !!seenAt })}
                        />
                      )}
                    </>
                  )}

                  <ReactionList
                    thumbsUp={thumbsUp}
                    thumbsDown={thumbsDown}
                    questionMark={questionMark}
                    showPopup={false}
                  />
                </div>
              </div>

              {isMyMessage ? (
                isPrevMyMessage && prevMsgUserId ? (
                  <div className="empty-avatar" />
                ) : (
                  <AvatarWithInitials
                    imageUrl={rightSideAvatar}
                    initials={rightSideName}
                    color={rightSideColor}
                  />
                )
              ) : null}
            </div>

            {errorIndicator && (
              <span className="chat-message__error-indicator">
                {t('errorIndicator')}
              </span>
            )}
          </>
        }
      >
        <span
          className="chat-message__time"
          onClick={(e) => e.stopPropagation()}
        >
          {getActionOnAtCopy('sentOn', createdAt)}
        </span>
        {showSeen && (
          <span
            className="chat-message__time"
            onClick={(e) => e.stopPropagation()}
          >
            {seenAt ? getActionOnAtCopy('seenOn', seenAt) : t('notSeenYet')}
          </span>
        )}
      </Collapsible>
      {!!showDate && (
        <div className="chat-message__date">
          <span className="chat-message__date__line" />
          <span className="chat-message__date__content">{showDate}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatMessage);
